import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PublicFooter: React.FC = () => {
  const { t } = useTranslation();

  return (
    <footer className="public-footer">
      <style>{`
        .public-footer {
          background: #1a1a2e;
          color: #fff;
        }
        .public-footer a {
          cursor: pointer;
        }

        /* ---- Top row: address + columns ---- */
        .footer-top {
          display: grid;
          grid-template-columns: 1.4fr 1fr 1fr 1fr;
          gap: 1.5rem;
          padding: 1.75rem 0 1.25rem;
          align-items: start;
        }
        .footer-col-heading {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 0.4rem;
          color: #fff;
        }
        .footer-col {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .footer-col a {
          display: inline-flex;
          align-items: center;
          min-height: 44px;
          color: #a0aec0;
          text-decoration: none;
          font-size: 0.82rem;
          padding: 0.12rem 0;
          transition: color 0.15s;
          width: fit-content;
        }
        .footer-col a:hover {
          color: #fff;
          text-decoration: none;
        }

        /* Address block in top row */
        .footer-address {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
        }
        .footer-address img {
          width: 22px;
          height: 22px;
          object-fit: contain;
          margin-top: 1px;
        }
        .footer-address-text {
          color: #a0aec0;
          font-size: 0.78rem;
          line-height: 1.55;
        }
        .footer-address-text strong {
          color: #fff;
          font-weight: 700;
          font-size: 0.82rem;
        }

        /* ---- Bottom bar: copyright | socials | legal ---- */
        .footer-bottom {
          border-top: 1px solid rgba(255,255,255,0.08);
          padding: 0.85rem 0;
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          gap: 0.75rem;
        }
        .footer-bottom small {
          color: #a0aec0;
          font-size: 0.72rem;
        }
        .footer-social {
          display: flex;
          gap: 0.85rem;
          align-items: center;
          justify-content: center;
        }
        .footer-social a {
          color: #a0aec0;
          font-size: 0.95rem;
          transition: color 0.15s;
          text-decoration: none;
          min-width: 44px;
          min-height: 44px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .footer-social a:hover {
          color: #fff;
        }
        .footer-bottom-links {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }
        .footer-bottom a {
          color: #a0aec0;
          text-decoration: none;
          font-size: 0.72rem;
          transition: color 0.15s;
        }
        .footer-bottom a:hover {
          color: #fff;
        }

        @media (max-width: 768px) {
          .footer-top {
            grid-template-columns: 1fr 1fr;
          }
          .footer-address img {
            display: none;
          }
          .footer-bottom {
            grid-template-columns: 1fr;
            text-align: left;
            gap: 0.5rem;
          }
          .footer-social {
            justify-content: flex-start;
          }
          .footer-bottom-links {
            justify-content: flex-start;
          }
        }
        @media (max-width: 480px) {
          .footer-top {
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
          }
          .footer-col {
            align-items: flex-start;
          }
        }
      `}</style>

      <div className="container">
        {/* ---- Top: address + columns on one line ---- */}
        <div className="footer-top">
          <div className="footer-address">
            <img src="/assets/ic_launcher_44.png" alt="DysGo" loading="lazy" width={22} height={22} />
            <div className="footer-address-text">
              <strong>DysGo</strong><br />
              {t('footer.company_name')}<br />
              {t('footer.address')}<br />
              {t('footer.email')}
            </div>
          </div>

          <div className="footer-col">
            <div className="footer-col-heading">{t('nav.product_offering')}</div>
            <a href="/#download">{t('nav.dysgo_app')}</a>
            <a href="/app/auth/login">{t('nav.dysgo_web')}</a>
          </div>

          <div className="footer-col">
            <div className="footer-col-heading">{t('nav.resources')}</div>
            <Link to="/knowledge">{t('nav.knowledge_base')}</Link>
            <Link to="/articles">{t('nav.articles')}</Link>
          </div>

          <div className="footer-col">
            <div className="footer-col-heading">{t('nav.about')}</div>
            <a href="/#about">{t('nav.company')}</a>
            <a href="/#events">{t('nav.events')}</a>
            <a href="/app/public/support">{t('nav.support')}</a>
          </div>
        </div>

        {/* ---- Bottom: copyright | socials | legal ---- */}
        <div className="footer-bottom">
          <small>{t('footer.copyright')}</small>
          <div className="footer-social">
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <i className="fab fa-facebook"></i>
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <i className="fab fa-linkedin"></i>
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
              <i className="fab fa-twitter"></i>
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <i className="fab fa-instagram"></i>
            </a>
          </div>
          <div className="footer-bottom-links">
            <a href="/privacy-policy">{t('footer.privacy_policy')}</a>
            <a href="/privacy-policy">{t('footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
